import type { ReactNode } from 'react';

/**
 * The one table in this application.
 *
 * Three accessibility rules are baked in rather than left to each surface,
 * because each of them is the kind of thing that is correct on the day it is
 * written and wrong three surfaces later (the CLM-51 bar):
 *
 *  1. **A caption, always.** Screen-reader table navigation announces the
 *     caption; a table without one is "table with 9 columns" and nothing else.
 *     It is visually hidden by default (`.vh`, already in shell.css) so the
 *     surface can keep its own visible heading.
 *  2. **`<th scope="col">` on every column.** Without `scope` a browser guesses
 *     the header association, and it guesses differently per browser.
 *  3. **No interactive element inside another one.** A clickable `<tr>` with a
 *     button in it is two nested activation targets, which is invalid and
 *     unusable by keyboard. So a row is never clickable: activation lives in
 *     **one** button in a dedicated last cell, and `activate` is what a surface
 *     supplies to build it.
 *
 * `aria-busy` is set while a page is loading so assistive technology knows the
 * rows it can see are stale, rather than being told nothing at all.
 */

export interface DataTableColumn<TRow> {
  /** Stable key. Also the column's React key. */
  readonly id: string;
  readonly header: string;
  readonly cell: (row: TRow) => ReactNode;
  /** Optional class on the `<td>`, e.g. for numeric alignment. */
  readonly cellClassName?: string;
}

export interface DataTableProps<TRow> {
  /** Announced to screen readers; visually hidden unless `captionVisible`. */
  readonly caption: string;
  readonly captionVisible?: boolean;
  readonly columns: readonly DataTableColumn<TRow>[];
  readonly rows: readonly TRow[];
  readonly rowKey: (row: TRow) => string;
  /** Marks a row as the currently open one, for `aria-current`. */
  readonly isCurrent?: (row: TRow) => boolean;
  /** True while a fetch is in flight, so the visible rows are stale. */
  readonly busy?: boolean;
  /** Shown in place of the rows when there are none. */
  readonly empty: ReactNode;
  /**
   * Bounds the table to a fixed-height box with its own vertical scrollbar
   * (header pinned to the top of that box, not the page) instead of letting
   * the table grow with its rows and the page scroll past it. Omit for the
   * default — most tables here are short enough that the page scrolling is
   * fine, and it stays that way unless a surface opts in.
   */
  readonly scrollable?: boolean;
  /**
   * The single per-row activation control. Supplying it adds one trailing
   * column holding exactly one `<button>`; omitting it makes the table inert.
   */
  readonly activate?: {
    readonly header: string;
    readonly label: (row: TRow) => string;
    readonly onActivate: (row: TRow) => void;
    /** DOM id for the row's button, so focus can be returned to it. */
    readonly buttonId: (row: TRow) => string;
  };
}

export function DataTable<TRow>({
  caption,
  captionVisible = false,
  columns,
  rows,
  rowKey,
  isCurrent,
  busy = false,
  empty,
  scrollable = false,
  activate,
}: DataTableProps<TRow>) {
  if (rows.length === 0 && !busy) {
    return <div className="dt-empty">{empty}</div>;
  }

  return (
    // The scroll container, not the page: a wide table must scroll inside its
    // own box rather than making the whole surface scroll sideways.
    <div className={scrollable ? 'dt-wrap dt-wrap--scroll' : 'dt-wrap'}>
      <table className="dt" aria-busy={busy}>
        <caption className={captionVisible ? 'dt-cap' : 'vh'}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.id} scope="col">
                {column.header}
              </th>
            ))}
            {activate ? <th scope="col">{activate.header}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const current = isCurrent?.(row) === true;
            return (
              <tr key={rowKey(row)} {...(current ? { 'aria-current': 'true' as const } : {})}>
                {columns.map((column) => (
                  <td key={column.id} {...(column.cellClassName ? { className: column.cellClassName } : {})}>
                    {column.cell(row)}
                  </td>
                ))}
                {activate ? (
                  <td>
                    <button
                      type="button"
                      id={activate.buttonId(row)}
                      className="btn sm"
                      onClick={() => {
                        activate.onActivate(row);
                      }}
                    >
                      {activate.label(row)}
                    </button>
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
